"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";

/**
 * LE BILAN GRATUIT, au cœur du hero.
 *
 * Une barre, trois gestes : le domaine, une adresse pro, « Voir mon bilan ».
 * Puis le seul moment animé de la page, et il répond à l'action de la
 * personne : pendant les trois à huit secondes d'analyse, la barre devient la
 * liste de ce qu'on est en train de lire — DNS, cadenas, page d'accueil,
 * Google — et coche au fur et à mesure. Rien n'est inventé : les étapes sont
 * celles du moteur, la dernière n'est cochée qu'à la vraie réponse.
 *
 * La case « RLCore peut me recontacter » est séparée et jamais pré-cochée :
 * c'est elle qui rend la relance licite, et c'est l'objet de l'aimant.
 */
const ETAPES = ["dns", "cadenas", "page", "google", "rapport"] as const;

/**
 * - `initialDomaine` : pré-rempli quand la personne vient de la barre du hero.
 * - `framed` : la carte au cadre dégradé (accueil) ; `false` pour l'intégrer
 *   dans un autre cadre (la modale de bilan).
 * - `showHeading` : titre + sous-titre de la carte ; la modale a les siens.
 * - `autoFocusEmail` : le domaine est déjà saisi, on amène sur le champ suivant.
 */
export function AuditForm({
  initialDomaine = "",
  framed = true,
  showHeading = true,
  autoFocusEmail = false,
}: {
  initialDomaine?: string;
  framed?: boolean;
  showHeading?: boolean;
  autoFocusEmail?: boolean;
} = {}) {
  const t = useTranslations("marketing.hero.audit");
  const locale = useLocale();
  const router = useRouter();
  const emailRef = useRef<HTMLInputElement>(null);
  const [domaine, setDomaine] = useState(initialDomaine);
  const [email, setEmail] = useState("");
  const [consentement, setConsentement] = useState(false);
  const [etat, setEtat] = useState<"idle" | "analyse" | "erreur">("idle");
  const [etape, setEtape] = useState(0);
  const [erreur, setErreur] = useState<string | null>(null);
  const minuteur = useRef<ReturnType<typeof setInterval> | null>(null);

  // Les étapes avancent au rythme réel du moteur (~1,2 s chacune) et
  // s'arrêtent avant la dernière : celle-là attend la réponse du serveur.
  useEffect(() => {
    if (etat !== "analyse") return;
    // Le compteur est remis à zéro dans `soumettre`, avant d'entrer ici :
    // l'effet ne fait que faire avancer les coches, il ne pose pas d'état.
    minuteur.current = setInterval(
      () => setEtape((e) => Math.min(e + 1, ETAPES.length - 2)),
      1200,
    );
    return () => {
      if (minuteur.current) clearInterval(minuteur.current);
    };
  }, [etat]);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setEtape(0);
    setEtat("analyse");
    try {
      const res = await fetch("/api/public/audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domaine, email, consentement }),
      });
      const data = (await res.json().catch(() => ({}))) as { jeton?: string; error?: string };
      if (!res.ok || !data.jeton) {
        setEtat("erreur");
        setErreur(
          data.error === "domaine"
            ? t("erreurDomaine")
            : data.error === "injoignable"
              ? t("erreurInjoignable")
              : res.status === 429
                ? t("erreurDebit")
                : t("erreur"),
        );
        return;
      }
      setEtape(ETAPES.length - 1);
      // Laisser la dernière coche se voir avant de partir.
      setTimeout(() => router.push(`/${locale}/bilan/${data.jeton}`), 450);
    } catch {
      setEtat("erreur");
      setErreur(t("erreur"));
    }
  }

  useEffect(() => {
    if (autoFocusEmail) emailRef.current?.focus();
  }, [autoFocusEmail]);

  const champ =
    "h-12 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-[15px] text-foreground placeholder:text-muted-foreground/70 outline-none transition-[border-color,box-shadow] duration-200 focus:border-[var(--primary)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--primary)_22%,transparent)]";

  const contenu = (
        <>
        {etat !== "analyse" ? (
          <form id="bilan" onSubmit={soumettre} aria-label={t("titre")} noValidate>
            {showHeading && (
              <>
                <h2 className="text-lg font-semibold tracking-tight text-foreground sm:text-xl">{t("titre")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t("sousTitre")}</p>
              </>
            )}

            <div className={showHeading ? "mt-5 grid gap-3 sm:grid-cols-[1.1fr_1fr_auto]" : "grid gap-3"}>
              <label className="sr-only" htmlFor="audit-domaine">{t("domaine")}</label>
              <input
                id="audit-domaine"
                type="text"
                inputMode="url"
                autoComplete="url"
                required
                value={domaine}
                onChange={(e) => setDomaine(e.target.value)}
                placeholder={t("domaine")}
                className={champ}
              />
              <label className="sr-only" htmlFor="audit-email">{t("email")}</label>
              <input
                ref={emailRef}
                id="audit-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("email")}
                className={champ}
              />
              {/* Piège à robots : invisible, jamais rempli par un humain. */}
              <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <button
                type="submit"
                className="primary-button rl-press h-12 rounded-xl px-6 text-[15px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--card,#171c1a)]"
              >
                {t("cta")}
              </button>
            </div>

            <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-[13px] leading-snug text-muted-foreground">
              <input
                type="checkbox"
                checked={consentement}
                onChange={(e) => setConsentement(e.target.checked)}
                className="mt-0.5 size-4 shrink-0 accent-[var(--primary)]"
              />
              <span>{t("consentement")}</span>
            </label>
            <p className="mt-3 text-[12px] leading-relaxed text-muted-foreground/80">{t("mention")}</p>

            {erreur && (
              <p role="alert" className="mt-3 rounded-lg border px-3 py-2 text-[13px]" style={{ borderColor: "color-mix(in srgb, var(--crit, #ff6b6b) 40%, transparent)", color: "var(--crit, #ff6b6b)" }}>
                {erreur}
              </p>
            )}
          </form>
        ) : (
          <div aria-live="polite">
            <h2 className="text-lg font-semibold tracking-tight text-foreground sm:text-xl">
              {t("analyseTitre", { domaine: domaine.replace(/^https?:\/\//, "").replace(/\/.*$/, "") })}
            </h2>
            <ol className="mt-5 space-y-2.5">
              {ETAPES.map((cle, i) => {
                const fait = i < etape || (i === ETAPES.length - 1 && etape === ETAPES.length - 1);
                const enCours = i === etape && !fait;
                return (
                  <li key={cle} className="flex items-center gap-3 text-[15px]" style={{ opacity: fait || enCours ? 1 : 0.4, transition: "opacity 300ms ease" }}>
                    <span
                      className="grid size-5 shrink-0 place-items-center rounded-full border"
                      style={{
                        borderColor: fait ? "var(--primary)" : "rgba(255,255,255,.18)",
                        background: fait ? "var(--primary)" : "transparent",
                        transition: "background 250ms ease, border-color 250ms ease",
                      }}
                      aria-hidden="true"
                    >
                      {fait ? (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#0f1211" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                      ) : enCours ? (
                        <span className="size-1.5 rounded-full bg-[var(--primary)] motion-safe:animate-pulse" />
                      ) : null}
                    </span>
                    <span className={fait ? "text-foreground" : "text-muted-foreground"}>{t(`etapes.${cle}`)}</span>
                  </li>
                );
              })}
            </ol>
            <p className="mt-4 text-[12px] text-muted-foreground/80">{t("analyseNote")}</p>
          </div>
        )}
        </>
  );

  if (!framed) return contenu;

  return (
    <div
      className="mx-auto w-full max-w-2xl rounded-2xl p-px"
      style={{
        background:
          "linear-gradient(135deg, color-mix(in srgb, var(--primary) 70%, transparent), #ffffff0f 55%, #7c5cfc80)",
      }}
    >
      <div className="relative rounded-[15px] bg-[var(--card,#171c1a)] p-5 text-left sm:p-6">
        {contenu}
      </div>
    </div>
  );
}
